import React, { useState, useEffect } from 'react';
import { Shield, Plus, Upload, UserX, UserCheck, Key, Trash2, Star, MessageSquare, Ban } from 'lucide-react';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where, getDoc, writeBatch, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import exercises from '../data/exercises.json';
import { sendPasswordResetEmail } from 'firebase/auth';
import { getAnalytics, logEvent } from 'firebase/analytics';

interface User {
  id: string;
  username?: string;
  email?: string;
  disabled?: boolean;
  isAdmin?: boolean;
  isBanned?: boolean;
  createdAt: string;
}

interface Post {
  id: string;
  content: string;
  authorId: string;
  authorEmail: string;
  authorUsername?: string;
  createdAt: string;
}

interface VisitorStats {
  totalVisitors: number;
  activeUsers: number;
  pageViews: number;
}

interface Report {
  id: string;
  postId: string;
  reportedBy: string;
  reportedUser: string;
  reason: string;
  content: string;
  createdAt: any;
  status: 'pending' | 'reviewed' | 'dismissed';
}

export default function AdminPortal() {
  const { user, isAdmin } = useAuth();
  const [newExercise, setNewExercise] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [activeTab, setActiveTab] = useState<'exercises' | 'users' | 'posts' | 'reports'>('users');
  const [reports, setReports] = useState<Report[]>([]);
  const [pinnedPostIds, setPinnedPostIds] = useState<Set<string>>(new Set());
  const [fixCommentCountLoading, setFixCommentCountLoading] = useState(false);
  const [fixCommentCountMessage, setFixCommentCountMessage] = useState('');
  const [visitorStats, setVisitorStats] = useState<VisitorStats>({
    totalVisitors: 0,
    activeUsers: 0,
    pageViews: 0
  });

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchPosts();
      fetchPinnedPosts();
      fetchVisitorStats();
      fetchReports();
      updatePageViews();
      
      // Set up interval to refresh stats every minute
      const interval = setInterval(fetchVisitorStats, 60000);
      return () => clearInterval(interval);
    }
    const analytics = getAnalytics();
    logEvent(analytics, 'admin_portal_view');

    // TODO: Implement function to fetch analytics data
    // fetchVisitorStats().then(data => setVisitorStats(data));
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      const usersQuery = query(collection(db, 'users'));
      const querySnapshot = await getDocs(usersQuery);
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as User[];
      
      // Sort users by username
      usersData.sort((a, b) => {
        const usernameA = a.username || a.email?.split('@')[0] || a.id;
        const usernameB = b.username || b.email?.split('@')[0] || b.id;
        return usernameA.localeCompare(usernameB);
      });
      
      setUsers(usersData);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
    }
  };

  const fetchPosts = async () => {
    try {
      const postsSnapshot = await getDocs(collection(db, 'posts'));
      const postsData = postsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Post[];
      
      // Fetch usernames for each post author
      const postsWithUsernames = await Promise.all(
        postsData.map(async (post) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', post.authorId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              return {
                ...post,
                authorUsername: userData.username || 'Unknown'
              };
            }
            return post;
          } catch (error) {
            console.error(`Error fetching username for user ${post.authorId}:`, error);
            return post;
          }
        })
      );
      
      setPosts(postsWithUsernames);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to fetch posts');
    }
  };

  const fetchPinnedPosts = async () => {
    try {
      const pinnedRef = collection(db, 'pinnedPosts');
      const snapshot = await getDocs(pinnedRef);
      setPinnedPostIds(new Set(snapshot.docs.map(doc => doc.data().postId)));
    } catch (error) {
      console.error('Error fetching pinned posts:', error);
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean = false) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isAdmin: !currentStatus
      });
      setUsers(users.map(user => 
        user.id === userId 
          ? { ...user, isAdmin: !currentStatus } 
          : user
      ));
      setError(null);
    } catch (err) {
      console.error('Error updating user status:', err);
      setError('Failed to update user status');
    }
  };

  const handleBanUser = async (userId: string, currentBanStatus: boolean = false) => {
    try {
      const userRef = doc(db, 'users', userId);
      
      // Update the user's ban status
      await updateDoc(userRef, {
        isBanned: !currentBanStatus
      });
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId 
          ? { ...user, isBanned: !currentBanStatus } 
          : user
      ));
      
      // Show feedback message
      if (!currentBanStatus) {
        alert(`User has been banned. They will be immediately logged out and shown a message explaining their account has been suspended.`);
      } else {
        alert(`User has been unbanned. They can now log in again.`);
      }
    } catch (err) {
      console.error('Error updating user ban status:', err);
      setError('Failed to update user ban status');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'users', userId));
      await fetchUsers(); // Refresh the list
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user');
    }
  };

  const handleToggleAdminStatus = async (userId: string, currentStatus: boolean = false) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isAdmin: !currentStatus
      });
      await fetchUsers(); // Refresh the list
    } catch (err) {
      console.error('Error toggling admin status:', err);
      setError('Failed to update admin status');
    }
  };

  const handleResetPassword = async (email: string) => {
    try {
      // Add actionCodeSettings to customize the password reset email
      const actionCodeSettings = {
        url: `${window.location.origin}/login`,
        handleCodeInApp: true
      };
      
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      alert('Password reset email sent successfully!');
    } catch (err) {
      console.error('Error sending password reset:', err);
      setError('Failed to send password reset email');
    }
  };

  const handleAddExercise = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin || !newExercise.trim() || loading) return;

    setLoading(true);
    setError(null);

    try {
      await addDoc(collection(db, 'exercises'), {
        title: newExercise.trim(),
        createdAt: new Date().toISOString()
      });
      setNewExercise('');
    } catch (err) {
      console.error('Error adding exercise:', err);
      setError('Failed to add exercise. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadExercises = async () => {
    if (!isAdmin || loading) return;
    
    setLoading(true);
    setError(null);

    try {
      for (const exercise of exercises) {
        await addDoc(collection(db, 'exercises'), {
          title: exercise,
          createdAt: new Date().toISOString()
        });
      }
      alert('Exercises uploaded successfully!');
    } catch (err) {
      console.error('Error uploading exercises:', err);
      setError('Failed to upload exercises. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'posts', postId));
      await fetchPosts(); // Refresh the list
    } catch (err) {
      console.error('Error deleting post:', err);
      setError('Failed to delete post');
    }
  };

  const handleDeleteAllUserPosts = async (userId: string, userEmail: string) => {
    if (!window.confirm(`Are you sure you want to delete all posts by ${userEmail}? This action cannot be undone.`)) {
      return;
    }

    try {
      const userPosts = posts.filter(post => post.authorId === userId);
      for (const post of userPosts) {
        await deleteDoc(doc(db, 'posts', post.id));
      }
      await fetchPosts(); // Refresh the list
      alert(`Successfully deleted all posts by ${userEmail}`);
    } catch (err) {
      console.error('Error deleting user posts:', err);
      setError('Failed to delete user posts');
    }
  };

  const handleTogglePin = async (postId: string) => {
    try {
      const pinnedRef = collection(db, 'pinnedPosts');
      const pinnedQuery = query(pinnedRef, where('postId', '==', postId));
      const pinnedSnapshot = await getDocs(pinnedQuery);
      
      if (pinnedSnapshot.empty) {
        // Pin the post
        const postDoc = await getDoc(doc(db, 'posts', postId));
        if (!postDoc.exists()) {
          throw new Error('Post not found');
        }
        
        await addDoc(pinnedRef, {
          postId,
          pinnedAt: new Date().toISOString()
        });
      } else {
        // Unpin the post
        await Promise.all(
          pinnedSnapshot.docs.map(doc => deleteDoc(doc.ref))
        );
      }
      
      // Refresh both lists
      await fetchPosts();
      await fetchPinnedPosts();
    } catch (err) {
      console.error('Error toggling pin status:', err);
      setError('Failed to update pin status');
    }
  };

  const fixCommentCount = async (postId: string) => {
    setFixCommentCountLoading(true);
    setFixCommentCountMessage('');
    try {
      // Get all comments for the post
      const commentsRef = collection(db, `posts/${postId}/comments`);
      const snapshot = await getDocs(commentsRef);
      
      console.log('Total documents in collection:', snapshot.size);
      snapshot.docs.forEach((doc, i) => {
        console.log(`Document ${i + 1}:`, doc.data());
      });
      
      // Filter out any deleted comments (they might still be in the collection)
      const validComments = snapshot.docs.filter(doc => {
        const data = doc.data();
        const isValid = data.content !== undefined && data.authorId !== undefined;
        console.log('Checking comment:', doc.id, 'isValid:', isValid, 'data:', data);
        return isValid;
      });
      
      const actualCommentCount = validComments.length;
      console.log('Actual valid comment count:', actualCommentCount);

      // First get the current post data
      const postRef = doc(db, 'posts', postId);
      const postDoc = await getDoc(postRef);
      console.log('Current post data:', postDoc.data());

      // Update the post's comment count
      await updateDoc(postRef, {
        comments: actualCommentCount
      });

      // If count was updated to 0, clean up any deleted comments
      if (actualCommentCount === 0) {
        console.log('Cleaning up deleted comments...');
        const batch = writeBatch(db);
        snapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
        await batch.commit();
      }

      setFixCommentCountMessage(`Successfully updated comment count to ${actualCommentCount}`);
    } catch (error) {
      console.error('Error fixing comment count:', error);
      setFixCommentCountMessage('Error fixing comment count. Check console for details.');
    } finally {
      setFixCommentCountLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const reports = JSON.parse(localStorage.getItem('reportedPosts') || '[]');
      setReports(reports);
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const handleReportAction = async (reportId: string, action: 'reviewed' | 'dismissed') => {
    try {
      const reports = JSON.parse(localStorage.getItem('reportedPosts') || '[]');
      const report = reports.find((r: any) => r.id === reportId);
      if (!report) {
        throw new Error('Report not found');
      }

      if (action === 'reviewed') {
        // Remove the post from Firestore
        const postRef = doc(db, 'posts', report.postId);
        await deleteDoc(postRef);
      }

      // Remove the report from localStorage
      const updatedReports = reports.filter((r: any) => r.id !== reportId);
      localStorage.setItem('reportedPosts', JSON.stringify(updatedReports));
      fetchReports();

      // Show feedback
      alert(action === 'reviewed' ? 
        'Report reviewed and post removed' : 
        'Report dismissed');
    } catch (err) {
      console.error('Error handling report:', err);
      setError('Failed to handle report: ' + (err instanceof Error ? err.message : String(err)));
    }
  };

  const forceResetCommentCount = async (postId: string) => {
    setFixCommentCountLoading(true);
    setFixCommentCountMessage('');
    try {
      // Force update the post's comment count to 0
      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        comments: 0
      });

      // Delete all comments
      const commentsRef = collection(db, `posts/${postId}/comments`);
      const snapshot = await getDocs(commentsRef);
      
      const batch = writeBatch(db);
      snapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      await batch.commit();

      setFixCommentCountMessage('Successfully reset comment count to 0');
    } catch (error) {
      console.error('Error resetting comment count:', error);
      setFixCommentCountMessage('Error resetting comment count. Check console for details.');
    } finally {
      setFixCommentCountLoading(false);
    }
  };

  // Function to fetch visitor stats from Firebase
  const fetchVisitorStats = async () => {
    try {
      const analyticsRef = collection(db, 'analytics');
      const visitorStatsDoc = await getDoc(doc(analyticsRef, 'visitorStats'));
      
      if (visitorStatsDoc.exists()) {
        const stats = visitorStatsDoc.data();
        setVisitorStats({
          totalVisitors: stats.totalVisitors || 0,
          activeUsers: stats.activeUsers || 0,
          pageViews: stats.pageViews || 0
        });
      }
    } catch (error) {
      console.error('Error fetching visitor stats:', error);
    }
  };

  // Function to update page views and active users
  const updatePageViews = async () => {
    try {
      const analyticsRef = collection(db, 'analytics');
      const visitorStatsRef = doc(analyticsRef, 'visitorStats');
      const userActivityRef = doc(analyticsRef, 'userActivity');
      const now = new Date().getTime();
      
      // Get current user ID for unique visitor tracking
      const visitorId = user?.uid || 'anonymous';
      
      // Update visitor stats
      const visitorStatsDoc = await getDoc(visitorStatsRef);
      const userActivityDoc = await getDoc(userActivityRef);
      
      const currentStats = visitorStatsDoc.exists() ? visitorStatsDoc.data() : { pageViews: 0, totalVisitors: 0, activeUsers: 0 };
      const userActivity = userActivityDoc.exists() ? userActivityDoc.data() : { visitors: {}, lastActive: {} };
      
      // Update last active time for current user
      userActivity.lastActive[visitorId] = now;
      
      // Clean up inactive users (inactive for more than 30 minutes)
      const activeThreshold = now - (30 * 60 * 1000);
      Object.keys(userActivity.lastActive).forEach(uid => {
        if (userActivity.lastActive[uid] < activeThreshold) {
          delete userActivity.lastActive[uid];
        }
      });
      
      // Track unique visitors
      if (!userActivity.visitors[visitorId]) {
        userActivity.visitors[visitorId] = true;
        currentStats.totalVisitors++;
      }
      
      // Update stats
      await updateDoc(visitorStatsRef, {
        pageViews: currentStats.pageViews + 1,
        totalVisitors: currentStats.totalVisitors,
        activeUsers: Object.keys(userActivity.lastActive).length
      });
      
      // Update user activity
      await setDoc(userActivityRef, userActivity);
      
    } catch (error) {
      console.error('Error updating visitor stats:', error);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-red-500">You do not have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-orange-500 flex items-center mb-4 sm:mb-0">
          <Shield className="mr-2" /> Admin Portal
        </h1>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-lg flex-1 sm:flex-none ${
              activeTab === 'users'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Users
          </button>
          <button
            onClick={() => setActiveTab('posts')}
            className={`px-4 py-2 rounded-lg flex-1 sm:flex-none ${
              activeTab === 'posts'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Posts
          </button>
          <button
            onClick={() => setActiveTab('exercises')}
            className={`px-4 py-2 rounded-lg flex-1 sm:flex-none ${
              activeTab === 'exercises'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Exercises
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`px-4 py-2 rounded-lg flex-1 sm:flex-none ${
              activeTab === 'reports'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Reports
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500">
          {error}
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-orange-500 mb-4">Reports Management</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-gray-700 rounded-lg overflow-hidden">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Reported Content</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                {reports.map(report => (
                  <tr key={report.id} className="hover:bg-gray-600">
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">{report.content}</div>
                      <div className="text-xs text-gray-400">Post ID: {report.postId}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">{report.reason}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        {report.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleReportAction(report.id, 'reviewed')}
                          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                          title="Review and Remove Post"
                        >
                          Remove Post
                        </button>
                        <button
                          onClick={() => handleReportAction(report.id, 'dismissed')}
                          className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
                          title="Dismiss Report"
                        >
                          Dismiss
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {reports.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-gray-400">
                      No pending reports
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'users' ? (
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-orange-500 mb-4">User Management</h2>
          <div className="overflow-x-auto -mx-6 sm:mx-0">
            <table className="min-w-full bg-gray-700 rounded-lg overflow-hidden">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">User</th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                {users.map(user => (
                  <tr key={user.id} className="hover:bg-gray-600">
                    <td className="px-2 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {user.photoURL && (
                          <img className="h-8 w-8 sm:h-10 sm:w-10 rounded-full mr-2 sm:mr-3" src={user.photoURL} alt="" />
                        )}
                        <div>
                          <div className="text-xs sm:text-sm font-medium text-white">{user.username || 'Anonymous User'}</div>
                          <div className="text-xs sm:text-sm text-gray-400 truncate max-w-[80px] sm:max-w-full">{user.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-300 truncate max-w-[80px] sm:max-w-full">{user.email}</div>
                    </td>
                    <td className="px-2 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col sm:flex-row sm:space-x-2 space-y-1 sm:space-y-0">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.isAdmin ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.isAdmin ? 'Admin' : 'User'}
                        </span>
                        {user.isBanned && (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            Banned
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-1 sm:space-x-2">
                        <button
                          onClick={() => handleToggleUserStatus(user.id, user.isAdmin)}
                          className={`px-2 py-1 rounded ${
                            user.isAdmin
                              ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                          title={user.isAdmin ? 'Remove admin' : 'Make admin'}
                        >
                          {user.isAdmin ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                        </button>
                        
                        <button
                          onClick={() => handleBanUser(user.id, user.isBanned)}
                          className={`px-2 py-1 rounded ${
                            user.isBanned
                              ? 'bg-green-600 hover:bg-green-700 text-white'
                              : 'bg-red-600 hover:bg-red-700 text-white'
                          }`}
                          title={user.isBanned ? 'Unban user' : 'Ban user'}
                        >
                          <Ban className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : activeTab === 'posts' ? (
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-orange-500 mb-4">Post Management</h2>
          <div className="overflow-x-auto -mx-6 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-700">
              <thead>
                <tr>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Content</th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Author</th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {posts.map((post) => (
                  <tr key={post.id}>
                    <td className="px-2 sm:px-6 py-4">
                      <div className="text-sm text-white line-clamp-2">{post.content}</div>
                    </td>
                    <td className="px-2 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-400">
                        <div className="font-medium text-white">{post.authorUsername || 'Unknown User'}</div>
                        <div className="text-xs text-gray-400">
                          <span className="text-gray-500">ID:</span> {post.authorId.substring(0, 8)}...
                        </div>
                        <div className="text-xs text-gray-400">{post.authorEmail}</div>
                      </div>
                    </td>
                    <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {new Date(post.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleTogglePin(post.id)}
                        className={`text-yellow-400 hover:text-yellow-300 ${pinnedPostIds.has(post.id) ? 'text-yellow-500' : ''}`}
                        title={`${pinnedPostIds.has(post.id) ? 'Unpin' : 'Pin'} Post`}
                      >
                        <Star size={20} />
                      </button>
                      <button
                        onClick={() => handleDeletePost(post.id)}
                        className="text-red-400 hover:text-red-300"
                        title="Delete Post"
                      >
                        <Trash2 size={20} />
                      </button>
                      <button
                        onClick={() => handleDeleteAllUserPosts(post.authorId, post.authorEmail)}
                        className="text-yellow-400 hover:text-yellow-300"
                        title="Delete All Posts by this User"
                      >
                        <MessageSquare size={20} />
                      </button>
                      <button
                        onClick={() => fixCommentCount(post.id)}
                        className="text-green-400 hover:text-green-300"
                        title="Fix Comment Count"
                      >
                        <Key size={20} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-orange-500 mb-4">Exercise Management</h2>
          <form onSubmit={handleAddExercise} className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="text"
                value={newExercise}
                onChange={(e) => setNewExercise(e.target.value)}
                placeholder="Enter new exercise..."
                className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="flex gap-2 sm:gap-4">
                <button
                  type="submit"
                  disabled={loading || !newExercise.trim()}
                  className="flex-1 sm:flex-none bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <Plus className="mr-2" size={20} />
                  <span className="whitespace-nowrap">Add Exercise</span>
                </button>
                <button
                  type="button"
                  onClick={handleUploadExercises}
                  disabled={loading}
                  className="flex-1 sm:flex-none bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <Upload className="mr-2" size={20} />
                  <span className="whitespace-nowrap">Upload All</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
      <section className="mt-8">
        <h2 className="text-xl font-bold mb-4">Visitor Statistics</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 bg-white rounded-lg shadow">
            <h3 className="text-gray-500">Total Visitors</h3>
            <p className="text-2xl font-bold">{visitorStats.totalVisitors}</p>
          </div>
          <div className="p-4 bg-white rounded-lg shadow">
            <h3 className="text-gray-500">Active Users</h3>
            <p className="text-2xl font-bold">{visitorStats.activeUsers}</p>
          </div>
          <div className="p-4 bg-white rounded-lg shadow">
            <h3 className="text-gray-500">Page Views</h3>
            <p className="text-2xl font-bold">{visitorStats.pageViews}</p>
          </div>
        </div>
      </section>
      <div className="bg-gray-800 dark:bg-gray-800 rounded-lg shadow p-6 mb-6 mt-6">
        <h2 className="text-xl font-semibold text-orange-500 mb-4">Fix Comment Count</h2>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Post ID
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Enter post ID"
              id="postIdInput"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => {
                const postId = (document.getElementById('postIdInput') as HTMLInputElement).value;
                if (postId) {
                  fixCommentCount(postId);
                } else {
                  setFixCommentCountMessage('Please enter a post ID');
                }
              }}
              disabled={fixCommentCountLoading}
              className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 whitespace-nowrap"
            >
              {fixCommentCountLoading ? 'Fixing...' : 'Fix Comment Count'}
            </button>
            <button
              onClick={() => {
                const postId = (document.getElementById('postIdInput') as HTMLInputElement).value;
                if (postId) {
                  forceResetCommentCount(postId);
                } else {
                  setFixCommentCountMessage('Please enter a post ID');
                }
              }}
              disabled={fixCommentCountLoading}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 whitespace-nowrap"
            >
              Force Reset to 0
            </button>
          </div>
        </div>
        {fixCommentCountMessage && (
          <p className={`mt-2 text-sm ${fixCommentCountMessage.includes('Error') ? 'text-red-500' : 'text-green-500'}`}>
            {fixCommentCountMessage}
          </p>
        )}
      </div>
    </div>
  );
}